// The delivery details as steps (UC-18, T7): the phone, then where the order
// goes for a saved customer, or everything at once for a new one. What
// Confirm would give the order is worked out here from what the steps hold,
// so the sheet and its tests agree on when Confirm is on. The server checks
// the same things again when the customer is saved.

export type Step = 'phone' | 'where' | 'new';

export interface SavedAddress {
	id: string;
	address: string;
	landmark?: string;
	/** Set only when the address was used from this shop. */
	areaId?: string;
}

export interface AddressForm {
	name: string;
	areaId: string;
	address: string;
	landmark: string;
}

export interface Steps {
	step: Step;
	/** The phone as typed on the keypad. */
	phone: string;
	/** On Where: adding an address rather than picking a saved one. */
	addrNew: boolean;
	/** On Where: the saved address picked. */
	pick: string;
	/** On Where: the area for a picked address that has none in this shop. */
	pickArea: string;
	form: AddressForm;
	/** The saved customer picked from the suggestions, on Where. */
	customer: { name: string; addresses: SavedAddress[] } | null;
}

/** What Confirm hands the order and the saved customer. */
export interface StepValues {
	phone: string;
	name: string;
	address: string;
	landmark: string;
	areaId: string;
}

export interface StepContext {
	/** This shop's delivery areas. With none, no area is ever asked for. */
	areaIds: string[];
	/** The phone as the server would store it, or empty when it does not read. */
	normalizedPhone: string;
	/** The phone already belongs to a saved customer. */
	phoneSaved: boolean;
}

export function freshSteps(phone = ''): Steps {
	return {
		step: 'phone',
		phone,
		addrNew: false,
		pick: '',
		pickArea: '',
		form: { name: '', areaId: '', address: '', landmark: '' },
		customer: null,
	};
}

/** The saved address Where has picked: the one tapped, or the last one used. */
export function pickedAddress(steps: Steps): SavedAddress | undefined {
	const addresses = steps.customer?.addresses ?? [];
	return addresses.find((a) => addressChoice(a) === steps.pick) ?? addresses[0];
}

/**
 * The key Where picks a saved address by: its id, or, for an address the
 * till's store kept offline, which has no id until the server's list comes
 * (#972), its addressKey. Two such addresses of one customer then stay two
 * choices. The key is the screen's alone and is never sent.
 */
export function addressChoice(address: SavedAddress): string {
	if (address.id) return address.id;
	return UNSYNCED + addressKey(address.address);
}

/**
 * What makes two spellings one address, as the server and the till's store
 * match a saved one: trimmed, its spaces collapsed, lower-cased. It mirrors
 * domain.AddressKey (internal/core/domain/customer.go), so the screen finds
 * the address the store answers for the one it confirmed. Screen-only: the
 * screen sends the address as typed, and the server keys it again.
 */
export function addressKey(address: string): string {
	return address.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Starts the key of an address with no id yet. */
const UNSYNCED = 'unsynced:';

/** The area a saved address brings, when it is one of this shop's. */
export function addressArea(address: SavedAddress, areaIds: string[]): string {
	return address.areaId && areaIds.includes(address.areaId) ? address.areaId : '';
}

/** "New customer" is on once the number reads and belongs to nobody saved. */
export function canStartNew(context: StepContext): boolean {
	return context.normalizedPhone !== '' && !context.phoneSaved;
}

/**
 * What Confirm would give, or null while something required is missing: an
 * address, an area when the shop has any, and for a new customer a name and a
 * number that is theirs.
 */
export function stepValues(steps: Steps, context: StepContext): StepValues | null {
	const needsArea = context.areaIds.length > 0;
	if (steps.step === 'phone') return null;

	if (steps.step === 'where' && steps.customer && !steps.addrNew) {
		const picked = pickedAddress(steps);
		if (!picked) return null;
		const areaId = needsArea ? addressArea(picked, context.areaIds) || steps.pickArea : '';
		if (needsArea && !areaId) return null;
		return {
			phone: steps.phone,
			name: steps.customer.name,
			address: picked.address,
			landmark: picked.landmark ?? '',
			areaId,
		};
	}

	const form = steps.form;
	const address = form.address.trim();
	if (!address) return null;
	if (needsArea && !form.areaId) return null;
	const isNew = steps.step === 'new';
	if (isNew && (!form.name.trim() || !canStartNew(context))) return null;
	return {
		phone: steps.phone,
		name: isNew ? form.name.trim() : (steps.customer?.name ?? ''),
		address,
		landmark: form.landmark.trim(),
		areaId: needsArea ? form.areaId : '',
	};
}
